import React, { FC, useState } from 'react';
import clsx from 'clsx';
import makeStyles from '@mui/styles/makeStyles';
import { FormControl, Theme, Grid, Button, TextField, InputAdornment, Divider, Autocomplete } from '@mui/material';
import { FilterList, Close, ArrowDropDown, ArrowDropUp, Search } from '@mui/icons-material';
import { IJob } from '../../../models';

interface IPurchaseOrdersFilters {
  isLoading: boolean;
  applyFilters: (clearFilters?: boolean) => void;
  setSearchValue: (val: string) => void;
  searchValue: string;
  jobs: IJob[];
  setSelectedJobSageId: (val: string) => void;
  selectedJobSageId: string;
  jobName: string;
  setJobName: (val: any) => void;
  setHasAppliedFilters: (val: boolean) => void;
  hasAppliedFilters?: boolean;
  setSearchedValue: (val: string) => void;
}

export const PurchaseOrdersFilters: FC<IPurchaseOrdersFilters> = ({
  jobs,
  setSelectedJobSageId,
  setSearchedValue,
  isLoading,
  applyFilters,
  setSearchValue,
  searchValue,
  jobName,
  setJobName,
  setHasAppliedFilters,
  hasAppliedFilters
}) => {
  const [isMobileFilterButtonOpen, toggleMobileFilter] = useState<boolean>(false);
  const classes = useStyles({ isMobileFilterButtonOpen });

  return (
    <>
      <Button
        color="secondary"
        variant="contained"
        className={classes.mobileButton}
        startIcon={<FilterList />}
        endIcon={isMobileFilterButtonOpen ? <ArrowDropUp /> : <ArrowDropDown />}
        onClick={() => {
          toggleMobileFilter(!isMobileFilterButtonOpen);
        }}
      >
        Filters
      </Button>
      {!isMobileFilterButtonOpen && <Divider className={classes.divider} />}
      <Grid container spacing={1} alignItems="center" className={classes.wrapper}>
        <Grid item xs={12} sm={6} md={4}>
          <TextField
            size="small"
            fullWidth
            variant="outlined"
            placeholder="Search..."
            name="search"
            value={searchValue}
            disabled={isLoading}
            InputProps={{
              startAdornment: (
                <InputAdornment
                  position="start"
                  className={classes.searchIcon}
                  onClick={() => {
                    if (searchValue.length > 0) {
                      setHasAppliedFilters(true);
                      setSearchedValue(searchValue);
                    }
                  }}
                >
                  <Search />
                </InputAdornment>
              ),
              endAdornment: searchValue ? (
                <InputAdornment
                  position="end"
                  className={classes.searchIcon}
                  onClick={() => {
                    setSearchValue('');
                    setSearchedValue('');
                    setHasAppliedFilters(false);
                  }}
                >
                  <Close />
                </InputAdornment>
              ) : null
            }}
            onKeyDown={e => {
              if (e.key === 'Enter' && searchValue.length > 0) {
                setHasAppliedFilters(true);
                setSearchedValue(searchValue);
              }
            }}
            onChange={e => {
              setSearchValue(e.target.value);
            }}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <FormControl fullWidth variant="outlined">
            <Autocomplete
              value={jobName ? jobName : ''}
              classes={{
                paper: classes.paperBorder
              }}
              onChange={(event, newValue: any) => {
                if (newValue) {
                  if (newValue && newValue.jobSageId) {
                    setSelectedJobSageId(newValue.jobSageId);
                    setJobName(`${newValue.jobName} - ${newValue.phase}`);
                  }
                } else {
                  setSelectedJobSageId('');
                }
              }}
              selectOnFocus
              clearOnBlur
              handleHomeEndKeys
              loading={isLoading}
              id="selectedJobSageId"
              options={jobs || []}
              getOptionLabel={(job: IJob) => {
                // Value selected with enter, right from the input
                if (typeof job === 'string') {
                  return job;
                }
                return `${job.jobName} - ${job.phase}`;
              }}
              renderInput={params => (
                <TextField
                  {...params}
                  InputLabelProps={{
                    className: classes.ellipsis
                  }}
                  key={params.id}
                  size="small"
                  autoComplete="off"
                  label="Job"
                  variant="outlined"
                />
              )}
            />
          </FormControl>
        </Grid>
        <Grid item xs={12} md={5}>
          <Button
            color="primary"
            variant="contained"
            className={classes.button}
            disabled={isLoading}
            startIcon={<FilterList />}
            onClick={() => {
              setSearchedValue(searchValue);
              setHasAppliedFilters(true);
              applyFilters();
            }}
          >
            Apply Filters
          </Button>
          {hasAppliedFilters && (
            <Button
              color="inherit"
              className={clsx(classes.button, classes.resetButton)}
              variant="contained"
              disabled={isLoading}
              startIcon={<Close />}
              onClick={() => {
                setHasAppliedFilters(false);
                setSearchValue('');
                setSearchedValue('');
                setJobName('');
                applyFilters(true);
              }}
            >
              Reset
            </Button>
          )}
        </Grid>
      </Grid>
    </>
  );
};

const useStyles = makeStyles<Theme, { isMobileFilterButtonOpen?: boolean }>((theme: Theme) => ({
  mobileButton: {
    width: '100%',
    [theme.breakpoints.up('sm')]: {
      display: 'none'
    }
  },
  button: {
    height: 40,
    textTransform: 'capitalize',
    width: '100%',
    '@media (min-width: 400px)': {
      width: '48%'
    },
    [theme.breakpoints.up('sm')]: {
      width: 'auto'
    }
  },
  resetButton: {
    '@media (min-width: 400px)': {
      marginLeft: 11
    },
    [theme.breakpoints.up('sm')]: {
      marginLeft: theme.spacing(1)
    }
  },
  wrapper: ({ isMobileFilterButtonOpen }) =>
    isMobileFilterButtonOpen
      ? {
          marginTop: 10,
          marginBottom: 10,
          display: 'flex'
        }
      : {
          display: 'none',
          marginBottom: theme.spacing(1),
          [theme.breakpoints.up('sm')]: {
            display: 'flex',
            marginBottom: theme.spacing(1)
          }
        },
  divider: {
    display: 'block',
    marginBottom: theme.spacing(1),
    [theme.breakpoints.up('sm')]: {
      display: 'none'
    }
  },
  gridItem: {
    padding: `3px 8px !important`,
    [theme.breakpoints.up('sm')]: {
      padding: `8px !important`
    }
  },
  searchIcon: {
    cursor: 'pointer',
    color: theme.palette.grey[500]
  },
  ellipsis: {
    fontSize: 14,
    width: 'auto',
    whiteSpace: 'normal',
    overflow: 'visible',
    '@media (min-width: 960px)': {
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
      overflow: 'hidden'
    },
    '@media (min-width: 1400px)': {
      width: 'auto',
      whiteSpace: 'normal',
      overflow: 'visible'
    }
  }
}));
